// RCPT1-S3 (docs/build-briefs/RCPT1-S3-receipts-and-awaiting-tip.md; PRD P0.9). The pure parts of the Awaiting tip sheet and the receipt
// Pay path: read the tip a guest wrote, work out what the sheet shows (the live "Tip $X, charge $Y", the limit, the over-limit choices)
// and the short words around them. No DOM, no state, so a unit test proves each rule directly.
//
// NO MONEY IS DECIDED HERE. The server owns the authorized amount, the cap and every capture; the figures below are the sheet's
// display of the numbers it was given plus the tip typed, added with integer cents. The server re-checks the cap before any
// provider call (RCPT1-S1), so a wrong figure here can only mislead the screen, never the charge.
//
// The one typed money field is the tip written on the receipt: the money-never-typed allowlist (e2e/usability.spec.ts) names this sheet's
// own field (awaiting-tip-input) and no other.

import { formatCents, parseDollarsToCentsExact } from "./money.ts";

/** The largest tip this sheet will read (cents). Far above any cap; it only stops a typo from becoming a number that overflows. */
export const MAX_TIP_ENTRY_CENTS = 99_999_999;

export type TipEntry = { kind: "empty" } | { kind: "invalid" } | { kind: "ok"; tipCents: number };

/** Reads what was typed. Blank is `empty` (nothing chosen yet), anything not a plain amount of dollars and cents is `invalid`. */
export function readTipEntry(raw: string): TipEntry {
  const text = raw.trim().replace(/^\$/, "");
  if (text.length === 0) return { kind: "empty" };
  try {
    const cents = parseDollarsToCentsExact(text, "Tip");
    if (cents > MAX_TIP_ENTRY_CENTS) return { kind: "invalid" };
    return { kind: "ok", tipCents: cents };
  } catch {
    return { kind: "invalid" };
  }
}

export interface TipSheetInput {
  authorizedCents: number;
  /** The most tip the server will capture, from the Awaiting tip state. */
  tipCapCents: number;
  entry: TipEntry;
  /** No tip was tapped: an explicit zero, as deliberate as a typed amount. */
  noTipPicked: boolean;
}

export interface TipSheetView {
  /** The tip as it stands (0 for No tip), or null while nothing is chosen or the entry is unreadable. */
  tipCents: number | null;
  /** What the card would be charged in all: the authorization plus the tip. Null while tipCents is null. */
  chargeCents: number | null;
  overCap: boolean;
  /** The one line under the amounts: "Tip $5.00, charge $30.13", the over-limit line, or empty. */
  liveLine: string;
  liveTone: "normal" | "danger";
  /** "Most you can add: $50.00". */
  capLine: string;
  /** The Charge button's label and whether it can be tapped. */
  chargeLabel: string;
  chargeEnabled: boolean;
  /** An unreadable entry, so the field can show it. */
  invalid: boolean;
}

export function tipSheetView(input: TipSheetInput): TipSheetView {
  const capLine = `Most you can add: ${formatCents(input.tipCapCents)}`;
  if (input.noTipPicked) {
    return {
      tipCents: 0,
      chargeCents: input.authorizedCents,
      overCap: false,
      liveLine: `No tip, charge ${formatCents(input.authorizedCents)}`,
      liveTone: "normal",
      capLine,
      chargeLabel: `Charge ${formatCents(input.authorizedCents)}`,
      chargeEnabled: true,
      invalid: false,
    };
  }
  if (input.entry.kind === "empty") {
    return {
      tipCents: null,
      chargeCents: null,
      overCap: false,
      liveLine: "",
      liveTone: "normal",
      capLine,
      chargeLabel: "Charge",
      chargeEnabled: false,
      invalid: false,
    };
  }
  if (input.entry.kind === "invalid") {
    return {
      tipCents: null,
      chargeCents: null,
      overCap: false,
      liveLine: "Enter dollars and cents, like 5.00",
      liveTone: "danger",
      capLine,
      chargeLabel: "Charge",
      chargeEnabled: false,
      invalid: true,
    };
  }
  const tip = input.entry.tipCents;
  if (tip > input.tipCapCents) {
    return {
      tipCents: tip,
      chargeCents: null,
      overCap: true,
      liveLine: `Tip ${formatCents(tip)}, over the limit`,
      liveTone: "danger",
      capLine,
      chargeLabel: "Charge tip",
      chargeEnabled: false,
      invalid: false,
    };
  }
  const charge = input.authorizedCents + tip;
  return {
    tipCents: tip,
    chargeCents: charge,
    overCap: false,
    liveLine: `Tip ${formatCents(tip)}, charge ${formatCents(charge)}`,
    liveTone: "normal",
    capLine,
    chargeLabel: `Charge ${formatCents(charge)}`,
    chargeEnabled: true,
    invalid: false,
  };
}

export interface OverCapChoices {
  title: string;
  /** A manager agrees to charge the limit as the tip. */
  capLabel: string;
  capTipCents: number;
  /** Charge the authorized amount with no tip. */
  noTipLabel: string;
}

/** The two choices offered over the limit, and nothing else. A second card charge for the rest is not offered (RCPT1-S3-F1). */
export function overCapChoices(authorizedCents: number, tipCapCents: number): OverCapChoices {
  return {
    title: `Over the ${formatCents(tipCapCents)} limit. A manager decides.`,
    capLabel: `Charge ${formatCents(tipCapCents)} tip, charge ${formatCents(authorizedCents + tipCapCents)}`,
    capTipCents: tipCapCents,
    noTipLabel: `No tip, charge ${formatCents(authorizedCents)}`,
  };
}

/** "Visa 4242", or the part that is known, or nothing. */
export function cardLabel(brand: string | null, last4: string | null): string {
  const name = brand === null || brand.trim() === "" ? "" : brand.trim();
  const title = name === "" ? "" : name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, " ");
  if (last4 === null || !/^[0-9]{4}$/.test(last4)) return title;
  return title === "" ? `Card ${last4}` : `${title} ${last4}`;
}

export type ReceiptPrintView =
  | { kind: "sent"; text: string }
  | { kind: "offline"; text: string; canSendElsewhere: boolean; printerNames: string[] };

/** What the rail says after a print answer. Never claims paper: a queued job is "sent", the printer acknowledges later. */
export function receiptPrintView(
  answer:
    | { outcome: "queued"; printer: { name: string } }
    | {
        outcome: "printer_offline";
        reason: "no_printer" | "offline";
        printers: ReadonlyArray<{ id: string; name: string; online: boolean }>;
      },
): ReceiptPrintView {
  if (answer.outcome === "queued") {
    return { kind: "sent", text: "Sent to printer" };
  }
  return {
    kind: "offline",
    text: "Printer offline",
    canSendElsewhere: answer.printers.length > 1,
    printerNames: answer.printers.map((printer) => printer.name),
  };
}

/** True when this sale takes its tip on the receipt: the location is in receipt mode, the payment is the whole ticket, by card, not on a
 * saved card or a tab's hold, and the ticket was not already told to use the reader (the authorization answered `reader`). */
export function isReceiptTipSale(input: {
  tipMode: "reader" | "receipt" | undefined;
  scopeIsWhole: boolean;
  tender: "card" | "cash" | undefined;
  savedCardChosen: boolean;
  activeHold: boolean;
  suppressedForThisTicket: boolean;
}): boolean {
  return (
    input.tipMode === "receipt" &&
    input.scopeIsWhole &&
    input.tender !== "cash" &&
    !input.savedCardChosen &&
    !input.activeHold &&
    !input.suppressedForThisTicket
  );
}

/** TERM10 (DEC-410). True when this card payment takes its tip on the reader, so the pay sheet shows no tip choice and sends
 * "no tip" from the device, and the reader asks. The server says whether the location's reader will ask (`readerTipOnReader`, the
 * preview); the device only adds what it alone knows: cash never tips, a saved card and a tab's hold never reach the reader's
 * tip screen, and a receipt sale (RCPT1-S3) takes its tip on paper. */
export function isReaderTipSale(input: {
  readerTipOnReader: boolean | undefined;
  tender: "card" | "cash" | undefined;
  savedCardChosen: boolean;
  activeHold: boolean;
  receiptSale: boolean;
}): boolean {
  return (
    input.readerTipOnReader === true &&
    input.tender !== "cash" &&
    !input.savedCardChosen &&
    !input.activeHold &&
    !input.receiptSale
  );
}
